import React from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { flexRender, Header, HeaderGroup } from '@tanstack/react-table';

import { cn } from '../../lib/utils';
import { TableHead, TableHeader, TableRow } from '../../ui';
import { ColumnResizeHandle } from './ColumnResizeHandle';
import { IDataTableHeaderProps } from '../DataTable.types';
import {
  getHeaderWidth,
  getSortIcon,
  getSortIconClassName
} from '../DataTable.utils';
import { SELECT_ALL_ROWS_ID } from '../DataTable.consts';

export const DataTableHeader = <T extends object>({
  table,
  stickyHeader,
  enableColumnResizing,
  showHeader
}: IDataTableHeaderProps<T>): React.ReactElement | null => {
  if (!showHeader) {
    return null;
  }

  const getHeaderClassName = (): string => {
    if (stickyHeader) {
      return 'sticky top-0 z-50 bg-white shadow-sm';
    }
    return '';
  };

  const renderSortingIcon = (header: Header<T, unknown>): React.ReactNode => {
    if (!header.column.columnDef.enableSorting) {
      return null;
    }

    return (
      <FontAwesomeIcon
        icon={getSortIcon(header)}
        className={getSortIconClassName(header)}
      />
    );
  };

  const renderColumnResizeHandle = (
    header: Header<T, unknown>
  ): React.ReactNode => {
    if (!enableColumnResizing) {
      return null;
    }
    return <ColumnResizeHandle header={header} />;
  };

  const getHeaderCellClassName = (header: Header<T, unknown>): string => {
    const classNames = [
      'py-4 px-4 text-base-mutedForeground text-p-s font-normal text-left align-top relative',
      'last:text-right'
    ];

    if (header.id === SELECT_ALL_ROWS_ID) {
      classNames.push('w-12');
    }

    if (header.column.columnDef.enableSorting) {
      classNames.push('cursor-pointer select-none');
    }

    if (enableColumnResizing) {
      classNames.push(
        'hover:border-r hover:border-base-border last:hover:border-r-0'
      );
    }

    if (stickyHeader) {
      classNames.push('bg-white');
    }

    return cn(...classNames);
  };

  const getHeaderClickHandler = (
    header: Header<T, unknown>
  ): ((event: unknown) => void) | undefined => {
    if (header.column.columnDef.enableSorting) {
      return header.column.getToggleSortingHandler();
    }
    return undefined;
  };

  const renderHeaderCell = (header: Header<T, unknown>): React.ReactNode => {
    return (
      <TableHead
        key={header.id}
        id={header.id}
        className={getHeaderCellClassName(header)}
        onClick={getHeaderClickHandler(header)}
        style={{
          width: getHeaderWidth(header)
        }}
      >
        <div className="flex items-center gap-2 group">
          {flexRender(header.column.columnDef.header, header.getContext())}
          {renderSortingIcon(header)}
        </div>
        {renderColumnResizeHandle(header)}
      </TableHead>
    );
  };

  const getRowClassName = (): string => {
    if (stickyHeader) {
      return 'border-b border-base-border';
    }
    return '';
  };

  const renderHeaderGroup = (headerGroup: HeaderGroup<T>): React.ReactNode => {
    return (
      <TableRow key={headerGroup.id} className={getRowClassName()}>
        {headerGroup.headers.map((header: Header<T, unknown>) =>
          renderHeaderCell(header)
        )}
      </TableRow>
    );
  };

  return (
    <TableHeader className={getHeaderClassName()}>
      {table
        .getHeaderGroups()
        .map((headerGroup: HeaderGroup<T>) => renderHeaderGroup(headerGroup))}
    </TableHeader>
  );
};
